/**
 * Обращения к серверу из кабинета клиента.
 *
 * Отдельный файл и отдельный ключ хранения намеренно. На одном компьютере
 * вполне может оказаться и сотрудник, и клиент — например, когда наш менеджер
 * проверяет, что видит клиент. Общий ключ означал бы, что вход в кабинет
 * выбрасывает из программы и наоборот.
 */

import { ApiError, apiBase, downloadFile } from '../api'
import type { BuildPreview, ClientProduct, CodeOrder, CodeOrderDetail } from '../types'

const TOKEN_KEY = 'timetrack.portal.token'

export function getPortalToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export function setPortalToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* приватный режим — сессия проживёт до закрытия вкладки */
  }
}

/** Адреса входа: сессии на них ещё нет, что бы ни лежало в хранилище. */
const SIGN_IN_PATHS = [
  '/api/portal/auth/request',
  '/api/portal/auth/verify',
  '/api/portal/invites/accept',
  '/api/portal/join',
]

const isSignIn = (path: string) =>
  SIGN_IN_PATHS.some((p) => path === p || path.startsWith(`${p}?`))

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  const token = getPortalToken()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  // FormData сам проставляет Content-Type с границей блоков. Поставь мы своё
  // значение — сервер не сможет разобрать файл, а ошибка будет невнятной.
  const isForm = init.body instanceof FormData
  if (init.body && !isForm && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const res = await fetch(`${apiBase()}${path}`, { ...init, headers })

  // Только там, где сессия была. На входе её ещё нет, и ответ сервера про
  // неверный код или просроченное приглашение должен дойти как есть.
  if (res.status === 401 && token && !isSignIn(path)) {
    setPortalToken(null)
    // Своё событие, не общее с программой: иначе истёкшая сессия клиента
    // выбрасывала бы из программы сотрудника в соседней вкладке.
    window.dispatchEvent(new CustomEvent('portal:unauthorized'))
    throw new ApiError(401, 'Сессия истекла, войдите заново')
  }
  if (!res.ok) {
    let detail = `Ошибка ${res.status}`
    try {
      const body = await res.json()
      if (typeof body.detail === 'string') detail = body.detail
      else if (Array.isArray(body.detail)) detail = body.detail.map((d: any) => d.msg).join('; ')
    } catch {
      /* тело не JSON — оставляем код */
    }
    throw new ApiError(res.status, detail)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export interface PortalMe {
  id: number
  full_name: string
  email: string
  position: string | null
  is_admin: boolean
  client_id: number
  client_name: string
  seats_total: number
  seats_used: number
  can_view_accesses: boolean
  /** Особые возможности клиента. По ним кабинет решает, какие разделы показывать. */
  features: string[]
}

export interface PortalUser {
  id: number
  client_id: number
  email: string
  full_name: string
  position: string | null
  phone: string | null
  is_admin: boolean
  is_active: boolean
  can_view_accesses: boolean
  created_at: string
  last_login_at: string | null
}

export interface PortalAccount {
  portal_user_id: number
  client_name: string
  is_admin: boolean
}

export interface PortalLogin {
  access_token: string | null
  accounts: PortalAccount[]
}

export interface JoinRequest {
  id: number
  email: string
  full_name: string
  position: string | null
  phone: string | null
  status: string
  created_at: string
}

/**
 * Итог приглашения. Ссылка приходит только если письмо не ушло: тогда её
 * передаёт руками тот, кто приглашал, — иначе человеку нечем войти.
 */
export type InviteSent = {
  invited: boolean
  mailed: boolean
  email: string
  link: string | null
}

export const portalApi = {
  requestCode: (email: string) =>
    request<{ sent: boolean }>('/api/portal/auth/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  verifyCode: (email: string, code: string, portalUserId?: number) =>
    request<PortalLogin>('/api/portal/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code, portal_user_id: portalUserId ?? null }),
    }),

  acceptInvite: (token: string) =>
    request<PortalLogin>('/api/portal/invites/accept', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  join: (body: Record<string, unknown>) =>
    request<{ accepted: boolean }>('/api/portal/join', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  me: () => request<PortalMe>('/api/portal/me'),
  users: () => request<PortalUser[]>('/api/portal/users'),

  invite: (body: { email: string; full_name: string; position?: string | null }) =>
    request<InviteSent>('/api/portal/users/invite', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateUser: (id: number, body: { is_active?: boolean; can_view_accesses?: boolean }) =>
    request<PortalUser>(`/api/portal/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  remove: (id: number) => request<void>(`/api/portal/users/${id}`, { method: 'DELETE' }),

  requests: () => request<JoinRequest[]>('/api/portal/requests'),
  approve: (id: number) =>
    request<InviteSent>(`/api/portal/requests/${id}/approve`, { method: 'POST' }),
  reject: (id: number) =>
    request<unknown>(`/api/portal/requests/${id}/reject`, { method: 'POST' }),
}

// --- обращения ---

export type TicketStatus =
  | 'new'
  | 'accepted'
  | 'in_progress'
  | 'waiting_client'
  | 'resolved'
  | 'closed'

export const TICKET_STATUS_LABEL: Record<TicketStatus, string> = {
  new: 'Новое',
  accepted: 'Принято',
  in_progress: 'В работе',
  waiting_client: 'Ждём вашего ответа',
  resolved: 'Решено',
  closed: 'Закрыто',
}

export interface TicketMessage {
  id: number
  body: string
  created_at: string
  author_name: string | null
  from_client: boolean
}

/**
 * Обращение или заявка.
 *
 * Обращение идёт по ежемесячной оплате — сопровождение и аренда 1С уже
 * оплачены тарифом. Заявка — разовая работа, деньги считаются по факту.
 */
export type TicketKind = 'request' | 'order'

/** Этап заявки. У обращений конвейера нет: там состояние, а не этапы. */
export type OrderStage =
  | 'new'
  | 'cards'
  | 'codes'
  | 'labels'
  | 'in_progress'
  | 'done'
  | 'paid'

export interface StageStep {
  stage: OrderStage
  title: string
  /** Пройден ли шаг и на нём ли заявка сейчас */
  done: boolean
  current: boolean
  at: string | null
}

/** Вид. От него зависит, какие поля спрашивают в форме. */
export type TicketCategory = 'support' | 'rent_1c' | 'codes' | 'work'

export const KIND_TITLE: Record<TicketKind, string> = {
  request: 'Обращение',
  order: 'Заявка',
}

export interface TicketFile {
  id: number
  original_name: string
  size: number
  uploaded_at: string
  /** Заполненный шаблон ГС1 — по нему считали количество кодов */
  is_template: boolean
  /** Результат работы: этикетки, акт, выгрузка — то, за чем клиент придёт */
  is_result: boolean
  uploaded_by: string | null
}

export interface Ticket {
  id: number
  client_id: number
  client_name: string | null
  subject: string
  description: string | null
  status: TicketStatus
  category: TicketCategory
  category_title: string
  kind: TicketKind
  kind_title: string
  program: string | null
  direction: string | null
  codes_count: number | null
  /** Конвейер заявки. У обращений пусто. */
  stage: OrderStage | null
  stage_title: string | null
  stage_changed_at: string | null
  due_on: string | null
  overdue: boolean
  /** Сколько часов согласовано по этой заявке */
  agreed_hours: number | null
  /** Оплата — отдельный факт, а не шаг конвейера */
  paid: boolean
  paid_at: string | null
  paid_by: string | null
  steps: StageStep[]
  author_name: string | null
  assignee_name: string | null
  created_at: string
  updated_at: string
  closed_at: string | null
  messages_count: number
  files_count: number
  waiting_for_us: boolean
}

export interface TicketDetail extends Ticket {
  messages: TicketMessage[]
  files: TicketFile[]
}

/** Доступен ли вид обращения и, если нет, почему. */
export interface CategoryAvailability {
  category: TicketCategory
  kind: TicketKind
  title: string
  available: boolean
  reason: string | null
}

export interface CodeTemplateGroup {
  group: string
  items: { key: string; title: string; version: string }[]
}

export interface TicketForm {
  categories: CategoryAvailability[]
  work_programs: string[]
  work_directions: string[]
  rent_directions: string[]
  rent_programs: { access_id: number; title: string }[]
  code_templates: CodeTemplateGroup[]
  max_file_mb: number
}

/** Что программа насчитала по заполненному шаблону — показывается до отправки. */
export interface TemplateCheck {
  upload_id: string
  file_name: string
  template_key: string
  template_title: string
  /** Карточек товара в файле. Кодов по ним заказывают сколько нужно. */
  rows: number
  /** Сумма колонки «Кол-во» — столько кодов и будет заказано. */
  codes_count: number
  /** Из-за чего заявку не примем: нет наименования или количества */
  problems: string[]
  /** Что ещё попросит ГС1. Отправку не держит — эти поля добирают по ходу */
  notes: string[]
  ok: boolean
}

export interface TicketCreate {
  subject: string
  description?: string | null
  category: TicketCategory
  program?: string | null
  direction?: string | null
  access_id?: number | null
  code_template?: string | null
  upload_id?: string | null
}

export const ticketsApi = {
  list: (kind: TicketKind, openOnly = false) => {
    const qs = new URLSearchParams({ kind })
    if (openOnly) qs.set('open_only', 'true')
    return request<Ticket[]>(`/api/portal/tickets?${qs}`)
  },
  get: (id: number) => request<TicketDetail>(`/api/portal/tickets/${id}`),
  form: (kind: TicketKind) =>
    request<TicketForm>(`/api/portal/tickets/form?kind=${kind}`),

  /**
   * Скачать пустой шаблон ГС1.
   *
   * Через fetch, а не ссылкой: ссылка не несёт токен, сервер отвечает отказом,
   * и браузер сохраняет этот отказ под именем шаблона — вместо таблицы
   * человек получает строчку JSON.
   */
  downloadTemplate: (key: string, name: string) =>
    downloadFile(
      `${apiBase()}/api/portal/tickets/templates/${encodeURIComponent(key)}`,
      name,
      getPortalToken(),
    ),

  checkTemplate: (key: string, file: File) => {
    const form = new FormData()
    form.append('key', key)
    form.append('file', file)
    return request<TemplateCheck>('/api/portal/tickets/templates/check', {
      method: 'POST',
      body: form,
    })
  },

  attach: (id: number, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<TicketDetail>(`/api/portal/tickets/${id}/files`, {
      method: 'POST',
      body: form,
    })
  },

  downloadAttachment: (ticketId: number, fileId: number, name: string) =>
    downloadFile(
      `${apiBase()}/api/portal/tickets/${ticketId}/files/${fileId}`,
      name,
      getPortalToken(),
    ),

  create: (body: TicketCreate) =>
    request<TicketDetail>('/api/portal/tickets', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  reply: (id: number, body: string) =>
    request<TicketDetail>(`/api/portal/tickets/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),
  close: (id: number) =>
    request<TicketDetail>(`/api/portal/tickets/${id}/close`, { method: 'POST' }),
}

// --- тариф и обзор ---

export interface Subscription {
  id: number
  client_id: number
  tariff_id: number
  tariff_name: string | null
  started_on: string
  ends_on: string | null
  price: string
  included_hours: string | null
  note: string | null
  is_current: boolean
  days_left: number | null
}

export interface Usage {
  hours_used: string
  hours_included: string | null
  hours_left: string | null
  period_from: string | null
  period_to: string | null
}

export interface TicketStats {
  total: number
  open_now: number
  resolved: number
  avg_first_response_hours: number | null
  by_author: Record<string, number>
  by_month: Record<string, number>
}

export interface Overview {
  client_name: string
  subscription: Subscription | null
  usage: Usage | null
  tickets: TicketStats
}

export const overviewApi = {
  get: () => request<Overview>('/api/portal/overview'),
}


// --- доступы ---

export interface AccessRow {
  id: number | null
  row_type: string
  url: string | null
  product_name: string | null
  computer: string | null
  user_name: string | null
  login: string | null
  password: string | null
}

export interface Access {
  id: number
  kind: string
  title: string
  login: string | null
  password: string | null
  server_address: string | null
  product_name: string | null
  service_name: string | null
  recovery_phone: string | null
  recovery_email: string | null
  note: string | null
  rows: AccessRow[]
  updated_at: string
}

export interface AccessViewLog {
  id: number
  access_title: string
  viewer_name: string
  created_at: string
}

export const accessesApi = {
  list: () => request<Access[]>('/api/portal/accesses'),
  // Пароль приходит только этим вызовом и только по одной записи: так утечка
  // одного ответа не становится утечкой всех доступов сразу.
  reveal: (id: number) =>
    request<Access>(`/api/portal/accesses/${id}/reveal`, { method: 'POST' }),
  log: () => request<AccessViewLog[]>('/api/portal/accesses/log'),
}

// --- мессенджер ---

/** Короткая выдержка из другой реплики — для ответа и пересылки. */
export interface QuotedMessage {
  id: number
  author_name: string | null
  body: string
  from_client: boolean
  /** Откуда переслали. У ответа пусто: он всегда в той же беседе. */
  conversation_title: string | null
}

export interface ChatMessage {
  id: number
  body: string
  created_at: string
  author_name: string | null
  /** Реплика вашей организации или наша */
  from_client: boolean
  file_name: string | null
  file_size: number | null
  reply_to: QuotedMessage | null
  forwarded_from: QuotedMessage | null
  edited_at: string | null
  /** Своя ли реплика — правят только свои */
  editable: boolean
}

export interface Conversation {
  id: number
  client_id: number
  client_name: string | null
  /** Привязка к обращению или заявке. Пусто — прямой разговор с сотрудником. */
  ticket_id: number | null
  ticket_kind: TicketKind | null
  ticket_subject: string | null
  staff_id: number | null
  staff_name: string | null
  /** Подпись в списке. Считается на сервере: зависит от того, кто смотрит. */
  title: string
  last_message_at: string | null
  last_from_client: boolean
  preview: string | null
  unread: number
  messages: ChatMessage[]
}

export interface StaffContact {
  id: number
  full_name: string
  role: string
}

export const chatApi = {
  list: () => request<Conversation[]>('/api/portal/conversations'),
  get: (id: number) => request<Conversation>(`/api/portal/conversations/${id}`),
  unread: () => request<{ unread: number }>('/api/portal/conversations/unread'),
  staff: () => request<StaffContact[]>('/api/portal/conversations/staff'),

  start: (staff_id: number | null) =>
    request<Conversation>('/api/portal/conversations', {
      method: 'POST',
      body: JSON.stringify({ staff_id }),
    }),

  /** Текст и файл уходят одной отправкой: иначе при сбое между двумя
   *  запросами сообщение осталось бы без вложения. */
  send: (
    id: number,
    body: string,
    file?: File | null,
    opts?: { replyTo?: number | null; forwardOf?: number | null },
  ) => {
    const form = new FormData()
    form.append('body', body)
    if (file) form.append('file', file)
    if (opts?.replyTo) form.append('reply_to', String(opts.replyTo))
    if (opts?.forwardOf) form.append('forward_of', String(opts.forwardOf))
    return request<Conversation>(`/api/portal/conversations/${id}/messages`, {
      method: 'POST',
      body: form,
    })
  },

  editMessage: (conversationId: number, messageId: number, body: string) =>
    request<Conversation>(
      `/api/portal/conversations/${conversationId}/messages/${messageId}`,
      { method: 'PATCH', body: JSON.stringify({ body }) },
    ),

  downloadFile: (conversationId: number, messageId: number, name: string) =>
    downloadFile(
      `${apiBase()}/api/portal/conversations/${conversationId}/files/${messageId}`,
      name,
      getPortalToken(),
    ),
}

// --- заказы кодов маркировки ---

/**
 * Поставки и коды по ним. Раздел появляется только у клиентов, которым
 * включён расширенный учёт: без него сервер не отдаст даже список.
 */
export const codeOrdersApi = {
  list: () => request<CodeOrder[]>('/api/portal/code-orders'),

  get: (id: number) => request<CodeOrderDetail>(`/api/portal/code-orders/${id}`),

  create: (body: { title: string; supply_date?: string; note?: string | null }) =>
    request<CodeOrderDetail>('/api/portal/code-orders', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  uploadSource: (id: number, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<CodeOrderDetail>(`/api/portal/code-orders/${id}/source`, {
      method: 'POST',
      body: form,
    })
  },

  confirm: (id: number) =>
    request<CodeOrderDetail>(`/api/portal/code-orders/${id}/confirm`, { method: 'POST' }),

  /** Свой справочник товаров: из него клиент и собирает поставку */
  products: (id: number) =>
    request<ClientProduct[]>(`/api/portal/code-orders/${id}/products`),

  buildPreview: (id: number, body: unknown) =>
    request<BuildPreview>(`/api/portal/code-orders/${id}/build/preview`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  build: (id: number, body: unknown) =>
    request<CodeOrderDetail>(`/api/portal/code-orders/${id}/build`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  download: (orderId: number, fileId: number, name: string) =>
    downloadFile(
      `${apiBase()}/api/portal/code-orders/${orderId}/files/${fileId}`,
      name,
      getPortalToken(),
    ),
}
